import { Healthcard } from 'interfaces';
import { Section, SectionProps } from './Section';
import { useGetModalSectionBorderColor } from 'hooks';
import { Card, Flex, Select } from '@mantine/core';
import { IconHealthRecognition } from '@tabler/icons-react';
import { useActiveForm } from 'pages/claims/components';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getValueByPath } from 'utils/getValueByPath';

interface ClaimHealthcardSectionProps extends SectionProps {
  healthcard?: Healthcard;
}

export const ClaimHealthcardSection = ({
  title,
  keys,
  cols,
  editComponentsMap,
  index,
  type = 'edit',
}: ClaimHealthcardSectionProps) => {
  const borderColor = useGetModalSectionBorderColor({
    keys,
  });
  const { form, prefix } = useActiveForm();
  const healthcardPath = prefix ? `${prefix}.healthcard` : 'healthcard';
  const healthcardIDPath = prefix ? `${prefix}.healthcardID` : 'healthcardID';
  const healthcardsPath = prefix
    ? `${prefix}.patient.healthcards`
    : 'patient.healthcards';

  const selectedHealthcardIDPath = prefix
    ? `${prefix}.healthcard.id`
    : 'healthcard.id';

  const patientPath = prefix ? `${prefix}.patient` : 'patient';
  const values = form.getValues();

  const [selectedHealthcardID, setSelectedHealthcardID] = useState<
    string | null
  >(
    getValueByPath(values, healthcardIDPath) ??
      getValueByPath(values, selectedHealthcardIDPath) ??
      null
  );

  useEffect(() => {
    const idFromForm = (getValueByPath(values, healthcardIDPath) ??
      getValueByPath(values, selectedHealthcardIDPath) ??
      null) as string | null;
    setSelectedHealthcardID(idFromForm);
  }, [selectedHealthcardIDPath, values]);

  const patientHealthcards = getValueByPath(values, healthcardsPath);

  const handleHealthcardChange = (value: string | null) => {
    setSelectedHealthcardID(value);
    if (!value) {
      form.setFieldValue(healthcardPath, null);
      form.setFieldValue(healthcardIDPath, null);
      form.validate();
      return;
    }

    if (value === 'add-new-healthcard') {
      const newHealthcard = {
        id: uuidv4(),
        number: '',
        registration: '',
        province: '',
        patientID: getValueByPath(values, patientPath)?.id,
      };
      form.setFieldValue(healthcardPath, newHealthcard);
      form.setFieldValue(healthcardIDPath, newHealthcard.id);
      form.validate();
      return;
    }
    const selected =
      patientHealthcards?.find((hc: Healthcard) => hc?.id === value) || null;
    form.setFieldValue(healthcardPath, selected);
    form.setFieldValue(healthcardIDPath, selected?.id ?? null);
    form.validate();
  };

  const selectOptions =
    patientHealthcards?.map((hc: Healthcard) => ({
      label: `${hc.number} - (${hc.registration})`,
      value: hc.id,
    })) || [];

  selectOptions &&
    selectOptions?.push({
      label: 'Add New Healthcard',
      value: 'add-new-healthcard',
    });

  return (
    <Card
      padding="md"
      mt="sm"
      shadow="xs"
      radius="md"
      withBorder
      style={{
        borderColor,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Flex direction="column" gap="xs" mt="sm">
        <Select
          placeholder="Select Healthcard"
          data={selectOptions}
          value={selectedHealthcardID}
          onChange={(value) => handleHealthcardChange(value)}
          searchable
          clearable
          style={{ width: '100%' }}
        />
        {selectedHealthcardID && (
          <Section
            key={selectedHealthcardID}
            index={prefix ? 'mrt-row-create' : index}
            title={title}
            icon={<IconHealthRecognition size={20} />}
            keys={keys}
            cols={cols}
            editComponentsMap={editComponentsMap}
            type={type}
          />
        )}
      </Flex>
    </Card>
  );
};
